// components/PhoneInputField.js
"use client";

import React from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { Controller } from "react-hook-form";

import { clearFieldErrors } from "@/lib/redux/slices/formSlice";
import getNestedError from "@/lib/utils/getNestedError";

function PhoneInputField({
  label,
  name,
  register,
  control,
  errors = null,
  setValue,
  clearErrors,
  className,
  float,
  labelPosition = "top",
  fieldHeight = "medium",
  fieldWidth = undefined, // undefined = full width (for form builder), or "small"/"medium"/"large" for customer form
  fieldAlignment = "left", // "left", "center", or "right"
  labelColor = null,
  ...props
}) {
  const dispatch = useDispatch();
  const errorMessage = getNestedError(errors, name);

  // Get height classes based on fieldHeight prop - must match InputField exactly
  const getHeightClasses = () => {
    switch (fieldHeight) {
      case "small":
        return "py-1.5 px-3 text-sm min-h-[36px]";
      case "large":
        return "py-3.5 px-4 text-base min-h-[56px]";
      case "medium":
      default:
        return "py-2 sm:py-2.5 px-4 text-sm sm:text-base min-h-[46px]";
    }
  };

  // Get width classes based on fieldWidth and fieldAlignment props
  // Only apply width restrictions if fieldWidth is explicitly provided
  // If not provided (form builder), use full width without restrictions
  const getWidthClasses = () => {
    if (!fieldWidth || fieldWidth === undefined) {
      return "w-full"; // Full width for form builder
    }
    
    // Get alignment margin class
    const alignmentMargin = fieldAlignment === "center" ? "mx-auto" : fieldAlignment === "right" ? "ml-auto" : "";
    
    switch (fieldWidth) {
      case "small":
        return `w-full max-w-sm ${alignmentMargin}`;
      case "large":
        return "w-full"; // Full width (matches container width)
      case "medium":
      default:
        return `w-full max-w-xl ${alignmentMargin}`;
    }
  };

  const heightClasses = getHeightClasses();
  const widthClasses = getWidthClasses();

  // Determine label rendering based on labelPosition
  const shouldShowLabel = label && float === "false" && labelPosition !== "placeholder";
  const shouldShowLabelTop = shouldShowLabel && labelPosition === "top";
  const shouldShowLabelLeft = shouldShowLabel && labelPosition === "left";
  // Only show placeholder when label position is "placeholder", otherwise no placeholder
  const placeholderText = labelPosition === "placeholder" && label ? label : (labelPosition === "placeholder" ? props.placeholder : "");

  const labelStyle = labelColor ? { color: labelColor } : { color: '#374151' }; // default text-gray-700

  return (
    <div className={`relative ${widthClasses}`}>
      {shouldShowLabelTop && (
        <label
          className="block text-sm sm:text-base mb-2 sm:mb-2.5"
          style={labelStyle}
          htmlFor={name}
        >
          {label}
        </label>
      )}
      <div className={shouldShowLabelLeft ? "flex items-center gap-3" : ""}>
        {shouldShowLabelLeft && (
          <label
            className="block text-sm sm:text-base whitespace-nowrap min-w-[120px]"
            style={labelStyle}
            htmlFor={name}
          >
            {label}
          </label>
        )}
        <div className={`relative ${shouldShowLabelLeft ? "flex-1 min-w-0" : ""}`}>
          <Controller
            name={name}
            control={control}
            render={({ field }) => (
              <div className={`phone-input-container w-full ${heightClasses} text-black border rounded-lg bg-white focus-within:outline-none focus-within:ring-2 autofill:!bg-white autofill:!shadow-[0_0_0px_40rem_white_inset] ${errors && errors[name] ? "border-red-500 focus-within:ring-red-500/50" : "border-gray-300 focus-within:ring-[#5D45EF]"}`}>
                <PhoneInput
                  {...register(name)}
                  {...field}
                  defaultCountry="AE"
                  onChange={(value) => {
                    field.onChange(value);
                    clearErrors(name);
                    dispatch(clearFieldErrors());
                  }}
                  className="phone-input-field"
                  placeholder={placeholderText}
                  {...props}
                />
              </div>
            )}
          />
        </div>
      </div>

      {errorMessage && (
        <p className={`text-red-500 text-sm mt-2.5 ${shouldShowLabelLeft ? "ml-[calc(120px+0.75rem)]" : ""}`}>
          {errorMessage}
        </p>
      )}
    </div>
  );
}
PhoneInputField.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  control: PropTypes.object.isRequired,
  errors: PropTypes.object,
  setValue: PropTypes.func.isRequired,
  clearErrors: PropTypes.func.isRequired,
  className: PropTypes.string,
  float: PropTypes.bool,
  register: PropTypes.func,
  labelPosition: PropTypes.oneOf(["top", "left", "placeholder"]),
  fieldHeight: PropTypes.oneOf(["small", "medium", "large"]),
  fieldWidth: PropTypes.oneOf(["small", "medium", "large", undefined]),
};

export default PhoneInputField;
